/**
 * Safely resolves an image URL by appending the backend origin if relative,
 * or rewriting hardcoded localhost:5000/5001 URLs to the correct active backend host.
 *
 * @param {string} url - The stored image URL/path.
 * @returns {string} The fully qualified image URL.
 */
export const getImageUrl = (url) => {
  if (!url) return "";
  
  // If it's a base64 string, data URL or external absolute URL (like Unsplash)
  if (url.startsWith("data:") || url.startsWith("https://") || url.startsWith("http://")) {
    // If it points to local development port (localhost:5000 or 5001)
    if (/https?:\/\/localhost:500[01]/.test(url)) {
      // In production, we want it to point to the current site's origin, or the configured API URL origin
      const apiBaseUrl = process.env.REACT_APP_API_URL || "";
      if (apiBaseUrl.startsWith("http://") || apiBaseUrl.startsWith("https://")) {
        try {
          const backendOrigin = new URL(apiBaseUrl).origin;
          return url.replace(/https?:\/\/localhost:500[01]/, backendOrigin);
        } catch (e) {}
      }
      
      // Fallback to window.location.origin in browser
      if (typeof window !== "undefined" && window.location) {
        return url.replace(/https?:\/\/localhost:500[01]/, window.location.origin);
      }
    }
    return url;
  }

  // If it's a path under uploads
  if (url.startsWith("/uploads") || url.startsWith("uploads")) {
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    
    // Check if we have an absolute API URL configuration to prepend
    const apiBaseUrl = process.env.REACT_APP_API_URL || "";
    if (apiBaseUrl.startsWith("http://") || apiBaseUrl.startsWith("https://")) {
      try {
        const backendOrigin = new URL(apiBaseUrl).origin;
        return `${backendOrigin}${cleanPath}`;
      } catch (e) {}
    }
    
    // Otherwise return root-relative path (which works perfectly on any page)
    return cleanPath;
  }

  // Treat other paths as frontend assets
  return url;
};

